import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { DollarSign, Cpu, Cloud, Coins, RefreshCw, ArrowLeft, AlertTriangle, Layers, Users } from 'lucide-react'
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts'
import { api, type FinopsSummary } from '@services/api'
import { toast } from 'react-toastify'

const PERIODS = [
  { label: '7 days', days: 7 },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
]

const usd = (n: number): string =>
  n >= 1
    ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 })
    : `$${n.toFixed(4)}`

const compactNum = (n: number): string => Intl.NumberFormat('en-US', { notation: 'compact' }).format(n)

const StatCard: React.FC<{
  icon: React.ReactNode
  label: string
  value: string
  sub?: string
  accent: string
}> = ({ icon, label, value, sub, accent }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:shadow-none">
    <div className="flex items-center justify-between">
      <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}</span>
      <span className={accent}>{icon}</span>
    </div>
    <div className="mt-2 text-2xl font-semibold text-gray-900 dark:text-gray-100">{value}</div>
    {sub && <div className="mt-1 text-xs text-gray-400 dark:text-gray-500">{sub}</div>}
  </div>
)

const CostBarRow: React.FC<{ label: string; cost: number; max: number; sub?: string; color: string }> = ({
  label,
  cost,
  max,
  sub,
  color,
}) => (
  <div className="py-2">
    <div className="flex items-center justify-between text-sm">
      <span className="truncate pr-2 text-gray-700 dark:text-gray-300" title={label}>
        {label}
      </span>
      <span className="font-medium whitespace-nowrap text-gray-900 dark:text-gray-100">{usd(cost)}</span>
    </div>
    <div className="mt-1 h-2 rounded bg-gray-100 dark:bg-gray-700">
      <div className={`h-2 rounded ${color}`} style={{ width: `${max > 0 ? Math.max(2, (cost / max) * 100) : 0}%` }} />
    </div>
    {sub && <div className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">{sub}</div>}
  </div>
)

const FinOpsDashboard: React.FC = () => {
  const navigate = useNavigate()
  const [days, setDays] = useState(30)
  const [data, setData] = useState<FinopsSummary | null>(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async (d: number) => {
    setLoading(true)
    try {
      const summary = await api.getFinopsSummary(d)
      setData(summary)
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to load FinOps data')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load(days)
  }, [days, load])

  const llm = data?.llm
  const azure = data?.azure
  const combined = (llm?.totalCost ?? 0) + (azure?.available ? azure.totalCost : 0)
  const maxModel = Math.max(1, ...(llm?.byModel.map((m) => m.cost) ?? [0]))
  const maxContext = Math.max(1, ...(llm?.byContext.map((c) => c.cost) ?? [0]))
  const maxUser = Math.max(1, ...(llm?.byUser.map((u) => u.cost) ?? [0]))

  return (
    <div className="mx-auto max-w-7xl px-4 py-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate('/admin')}
            className="mb-1 flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
          >
            <ArrowLeft className="mr-1 h-4 w-4" /> Admin
          </button>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900 dark:text-gray-100">
            <DollarSign className="h-6 w-6 text-emerald-600" /> FinOps — Cost Dashboard
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">AI/LLM spend and Azure infrastructure costs.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex overflow-hidden rounded-md border border-gray-200 dark:border-gray-700">
            {PERIODS.map((p) => (
              <button
                key={p.days}
                onClick={() => setDays(p.days)}
                className={`px-3 py-1.5 text-sm ${
                  days === p.days
                    ? 'bg-emerald-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-900'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => load(days)}
            className="rounded-md border border-gray-200 p-2 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-900"
            title="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {loading && !data ? (
        <div className="py-20 text-center text-gray-400 dark:text-gray-500">Loading cost data…</div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              icon={<DollarSign className="h-5 w-5" />}
              accent="text-emerald-600"
              label="Combined spend"
              value={usd(combined)}
              sub={`AI (${days}d) + Azure (MTD)`}
            />
            <StatCard
              icon={<Cpu className="h-5 w-5" />}
              accent="text-indigo-600"
              label={`AI / LLM spend (${days}d)`}
              value={usd(llm?.totalCost ?? 0)}
              sub={`${compactNum(llm?.totalCalls ?? 0)} calls · ${compactNum(
                (llm?.totalInputTokens ?? 0) + (llm?.totalOutputTokens ?? 0),
              )} tokens`}
            />
            <StatCard
              icon={<Cloud className="h-5 w-5" />}
              accent="text-sky-600"
              label="Azure infra (MTD)"
              value={azure?.available ? usd(azure.totalCost) : '—'}
              sub={azure?.available ? `${azure.byService.length} services · ${azure.currency}` : 'unavailable'}
            />
            <StatCard
              icon={<Coins className="h-5 w-5" />}
              accent="text-amber-600"
              label="Tokens in / out"
              value={`${compactNum(llm?.totalInputTokens ?? 0)} / ${compactNum(llm?.totalOutputTokens ?? 0)}`}
              sub={`over ${days} days`}
            />
          </div>

          {/* AI daily trend */}
          <div className="mb-6 rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:shadow-none">
            <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
              <Cpu className="h-4 w-4 text-indigo-600" /> AI spend trend (daily)
            </h2>
            {llm && llm.daily.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={llm.daily} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                  <Tooltip formatter={(v) => usd(Number(v))} />
                  <Line type="monotone" dataKey="cost" stroke="#4f46e5" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="py-8 text-center text-sm text-gray-400 dark:text-gray-500">
                No AI usage recorded in this period.
              </p>
            )}
          </div>

          {/* AI breakdowns */}
          <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:shadow-none">
              <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                <Layers className="h-4 w-4 text-indigo-600" /> By model
              </h2>
              {llm?.byModel.length ? (
                llm.byModel.map((m) => (
                  <CostBarRow
                    key={m.model}
                    label={m.model}
                    cost={m.cost}
                    max={maxModel}
                    color="bg-indigo-500"
                    sub={`${compactNum(m.calls)} calls · ${compactNum(m.inputTokens + m.outputTokens)} tokens`}
                  />
                ))
              ) : (
                <p className="py-6 text-center text-sm text-gray-400 dark:text-gray-500">No data</p>
              )}
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:shadow-none">
              <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                <Layers className="h-4 w-4 text-emerald-600" /> By context
              </h2>
              {llm?.byContext.length ? (
                llm.byContext.map((c) => (
                  <CostBarRow key={c.label} label={c.label} cost={c.cost} max={maxContext} color="bg-emerald-500" />
                ))
              ) : (
                <p className="py-6 text-center text-sm text-gray-400 dark:text-gray-500">No data</p>
              )}
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:shadow-none">
              <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                <Users className="h-4 w-4 text-amber-600" /> Top users
              </h2>
              {llm?.byUser.length ? (
                llm.byUser.map((u) => (
                  <CostBarRow key={u.label} label={u.label} cost={u.cost} max={maxUser} color="bg-amber-500" />
                ))
              ) : (
                <p className="py-6 text-center text-sm text-gray-400 dark:text-gray-500">No data</p>
              )}
            </div>
          </div>

          {/* Azure infra */}
          <div className="mb-6 rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:shadow-none">
            <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
              <Cloud className="h-4 w-4 text-sky-600" /> Azure infrastructure — by service (month-to-date)
            </h2>
            {azure?.available ? (
              azure.byService.length > 0 ? (
                <ResponsiveContainer width="100%" height={Math.max(200, azure.byService.length * 34)}>
                  <BarChart
                    data={azure.byService}
                    layout="vertical"
                    margin={{ top: 5, right: 20, left: 10, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                    <YAxis type="category" dataKey="label" width={160} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => usd(Number(v))} />
                    <Bar dataKey="cost" fill="#0284c7" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="py-8 text-center text-sm text-gray-400 dark:text-gray-500">
                  No Azure costs reported yet this month.
                </p>
              )
            ) : (
              <div className="flex items-start gap-2 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700 dark:border-amber-800/50 dark:bg-amber-900/30 dark:text-amber-300">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <div>
                  Azure cost data unavailable. The backend managed identity needs the{' '}
                  <span className="font-medium">Cost Management Reader</span> role on the resource group.
                  {azure?.error && <div className="mt-1 text-xs text-amber-600">{azure.error}</div>}
                </div>
              </div>
            )}
          </div>

          {data?.pricingNote && <p className="text-xs text-gray-400 dark:text-gray-500">{data.pricingNote}</p>}
        </>
      )}
    </div>
  )
}

export default FinOpsDashboard
